import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

const inputClass = "w-full h-12 px-4 rounded-xl bg-slate-50/60 border border-slate-200 text-slate-900 placeholder:text-slate-400 outline-none focus:bg-white focus:border-slate-400 focus:ring-4 focus:ring-slate-900/5 dark:bg-white/5 dark:border-white/10 dark:text-slate-100";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!value.trim()) { toast.error("Veuillez saisir votre e-mail ou identifiant."); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { identifiant: value.trim() });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[#FAFAFA] p-4 dark:bg-[#0A0A0A]">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:border-white/10 dark:bg-white/[0.04]"
        data-testid="forgot-card">
        <div className="mb-6 flex items-center gap-2.5"><Logo size={34} /><span className="text-base font-semibold text-slate-900 dark:text-slate-100">Le Passeport</span></div>
        {sent ? (
          <div className="flex flex-col items-center gap-4 py-4 text-center" data-testid="forgot-sent">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-500/10"><MailCheck size={30} /></div>
            <p className="text-sm text-slate-500">Si un compte correspond, un e-mail de réinitialisation vient d'être envoyé. Vérifiez votre boîte de réception.</p>
            <button onClick={() => navigate("/login")} className="text-sm font-semibold text-slate-900 hover:underline dark:text-slate-100" data-testid="forgot-to-login">Retour à la connexion</button>
          </div>
        ) : (
          <>
            <button onClick={() => navigate("/login")} className="mb-4 flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600" data-testid="forgot-back"><ArrowLeft size={15} /> Connexion</button>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Mot de passe oublié</h1>
            <p className="mt-1 mb-5 text-sm text-slate-500">Saisissez votre e-mail : un lien de réinitialisation vous sera envoyé.</p>
            <form onSubmit={submit} className="flex flex-col gap-3">
              <input className={inputClass} placeholder="Adresse e-mail ou identifiant" value={value} onChange={(e) => setValue(e.target.value)} data-testid="forgot-input" />
              <button type="submit" disabled={busy} className="mt-1 flex h-12 w-full items-center justify-center rounded-xl bg-slate-900 font-medium text-white transition-transform hover:scale-[0.98] disabled:opacity-60 dark:bg-white dark:text-slate-900" data-testid="forgot-submit">
                {busy ? <Loader2 size={20} className="animate-spin" /> : "Envoyer le lien"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
